import { Module } from "@/constants";
import { userPermissionKeys } from "@/modules/users/permissions/userPermissions";
import { projectPermissionKeys } from "@/modules/projects/permissions/projectPermissions";
import { contractorVisitPermissionKeys } from "@/modules/projects/permissions/contractorVisitPermissions";
import { roleBasedAssignmentsPermissionKeys } from "@/modules/projects/permissions/roleBasedAssignmentsPermissions";
import { agreementPermissionKeys } from "@/modules/agreement/permissions/agreementsPermissions";
import { structureAssignmentsPermissionKeys } from "@/modules/structure-assignments/permissions/structureAssignmentsPermissions";
import { contractorPermissionKeys } from "@/modules/contractor/permissions/contractorPermissions";
import type { UserPermissions } from "@/modules/users/permissions/userPermissions";
import type { ProjectPermissions } from "@/modules/projects/permissions/projectPermissions";
import type { ContractorVisitPermissions } from "@/modules/projects/permissions/contractorVisitPermissions";
import type { RoleBasedAssignmentsPermissions } from "@/modules/projects/permissions/roleBasedAssignmentsPermissions";
import type { AgreementPermissions } from "@/modules/agreement/permissions/agreementsPermissions";
import type { StructureAssignmentsPermissions } from "@/modules/structure-assignments/permissions/structureAssignmentsPermissions";
import type { ContractorPermissions } from "@/modules/contractor/permissions/contractorPermissions";

export interface DefaultPermissions {
  can_read: boolean;
  can_create: boolean;
  can_export: boolean;
  can_update: boolean;
  can_delete: boolean;
}

export const moduleCustomPermissions: Partial<
  Record<Module, readonly string[]>
> = {
  [Module.Users]: userPermissionKeys,
  [Module.Projects]: projectPermissionKeys,
  [Module.ContractorVisits]: contractorVisitPermissionKeys,
  [Module.RoleBasedAssignments]: roleBasedAssignmentsPermissionKeys,
  [Module.Agreements]: agreementPermissionKeys,
  [Module.StructureAssignments]: structureAssignmentsPermissionKeys,
  [Module.Contractors]: contractorPermissionKeys,
};

export interface ModulePermissionsMap {
  [Module.Users]: UserPermissions;
  [Module.Projects]: ProjectPermissions;
  [Module.ContractorVisits]: ContractorVisitPermissions;
  [Module.RoleBasedAssignments]: RoleBasedAssignmentsPermissions;
  [Module.Agreements]: AgreementPermissions;
  [Module.StructureAssignments]: StructureAssignmentsPermissions;
  [Module.Contractors]: ContractorPermissions;
  [key: string]: DefaultPermissions;
}

export type ModulePermissionShape<T extends readonly string[]> =
  DefaultPermissions & Record<T[number], boolean>;

export type Permissions<T extends DefaultPermissions = DefaultPermissions> = {
  [key in Module]: T;
};

export const modulePermissionRules: Partial<
  Record<
    Module,
    {
      can_read?: string[];
      can_create?: string[];
      can_update?: string[];
      can_delete?: string[];
      can_export?: string[];
    }
  >
> = {
  [Module.Projects]: {
    can_read: ["projects_read_basic", "projects_read_full"],
  },
  [Module.ContractorVisits]: {
    can_read: ["contractor_visits_read_basic", "contractor_visits_read_full"],
  },
  [Module.StructureAssignments]: {
    can_read: [
      "structure_assignments_rr_to_rd_read",
      "structure_assignments_rr_to_fam_read",
      "structure_assignments_kam_to_csd_read",
    ],
  },
  [Module.RoleBasedAssignments]: {
    can_export: ["role_based_project_rates_export"],
  },
};

export enum Operation {
  Read = "can_read",
  Export = "can_export",
  Create = "can_create",
  Update = "can_update",
  Delete = "can_delete",
}
